// Hero Class Progression System
import { BRANCHES } from './data';

// ─── TIER PROGRESSION ────────────────────────────────────────────────────────
export const HERO_TIERS = [
  { tier: 0, name: 'Squire',    icon: '⚔️',  color: '#94a3b8', minPoints: 0,   maxPoints: 19,      description: 'A novice warrior beginning their journey. You\'ve mastered the basics of footwork and grip. Your foundation is solid, but the real battles lie ahead. Keep training to earn your first victories.' },
  { tier: 1, name: 'Knight',    icon: '🛡️',  color: '#3b82f6', minPoints: 20,  maxPoints: 49,      description: 'You\'ve proven your worth in combat. Your defensive skills are sharp, and you can handle offensive pressure. You understand positioning and are developing consistent technique across multiple shots.' },
  { tier: 2, name: 'Champion',  icon: '⚡',   color: '#3b5bb5', minPoints: 50,  maxPoints: 99,      description: 'A true warrior of skill and honor. You dominate the court with tactical awareness and powerful shot selection. Your consistency is unmatched, and opponents fear your aggressive play. Victory follows your blade.' },
  { tier: 3, name: 'Warlord',   icon: '🏆',  color: '#ec4899', minPoints: 100, maxPoints: 149,     description: 'You command the court like a seasoned general. Your mastery spans multiple techniques—precise defense, explosive attacks, and calculated strategy. Opponents crumble before your tactical brilliance and relentless pressure.' },
  { tier: 4, name: 'Conqueror', icon: '👑',  color: '#f59e0b', minPoints: 150, maxPoints: 199,     description: 'An elite warrior who bends the game to their will. Your arsenal is nearly complete—every shot deadly, every movement purposeful. You don\'t just win matches; you reshape how the game is played. Legions follow your tactics.' },
  { tier: 5, name: 'War God',   icon: '💎',  color: '#fbbf24', minPoints: 200, maxPoints: Infinity, description: 'Transcendent mastery beyond mortal reach. You\'ve achieved what only legends accomplish—perfecting every aspect of your craft. Your opponents face not just a player, but a force of nature. Your name will echo through the halls of history forever.' },
];

// ─── CLASS METADATA ───────────────────────────────────────────────────────────
export const HERO_CLASSES = {
  guardian:   {
    label: 'Guardian',
    description: 'The impenetrable wall of the court. You absorb pressure, convert defense into counterattacks, and wear down opponents through unwavering resilience. Your strength is not in aggression, but in the unbreakable spirit that refuses to yield.'
  },
  berserker:  {
    label: 'Berserker',
    description: 'Pure offensive fury unleashed. You overwhelm opponents with explosive power and relentless attacking shots. Every stroke is a statement—aggressive, bold, and designed to end points decisively. Your game is controlled chaos.'
  },
  strategist: {
    label: 'Strategist',
    description: 'The calculating commander of tempo. You orchestrate rallies, outthink opponents, and execute perfect positioning. Your game is a chess match—every move calculated, every shot purposeful. Victory belongs to the prepared mind.'
  },
  duelist:    {
    label: 'Duelist',
    description: 'The complete warrior—equally deadly on offense and defense. You adapt flawlessly between attacking and defending, striking when opportunities arise and holding ground when needed. True mastery knows no single weakness.'
  },
};

// ─── TIER STORY NARRATIVES ────────────────────────────────────────────────────
// Personalized journey narratives for each class at each tier
export const TIER_STORIES = {
  guardian: [
    {
      tier: 0,
      name: 'Squire',
      walked: 'You\'ve taken your first steps on the court, learning to stand firm and move with purpose. Your foundation is solid—grip, footwork, positioning.',
      ahead: 'The real challenge awaits: converting that defensive foundation into counterattacking opportunities. Master the art of turning pressure into power.'
    },
    {
      tier: 1,
      name: 'Knight',
      walked: 'You\'ve proven you can absorb punishment and keep fighting. Your opponents have learned: you don\'t break easily. Defensive mechanics are becoming second nature.',
      ahead: 'Now learn to close out points from defensive positions. Your consistency is your weapon—make opponents fear your patience and precision.'
    },
    {
      tier: 2,
      name: 'Champion',
      walked: 'You dominate through defensive mastery. Rally after rally, you wear opponents down. They attack; you defend with purpose. Your resilience is legendary.',
      ahead: 'Elevate your game by finishing points you\'ve earned. Combine your defensive prowess with calculated offensive strikes—become unstoppable.'
    },
    {
      tier: 3,
      name: 'Warlord',
      walked: 'You command the court like a general. Your opponents face an impenetrable wall that never crumbles. You\'ve mastered multiple defensive systems and turned them into an art.',
      ahead: 'Few remain at this level. Master the balance between holding ground and strategic offense. Your wisdom on the court will shape how future players defend.'
    },
    {
      tier: 4,
      name: 'Conqueror',
      walked: 'You are the guardian immortal. Your defense transcends technique—it\'s a philosophy. Every opponent who faces you learns humility. Your legacy is written in every rally.',
      ahead: 'The final ascent awaits. Perfect every nuance. Become the player legends speak of in whispered tones.'
    },
    {
      tier: 5,
      name: 'War God',
      walked: 'You have achieved what mortals only dream of. Your defense is absolute. You stand alone at the pinnacle—the greatest guardian ever to grace the court. History remembers your name.',
      ahead: 'There is no higher peak. You have reached transcendence. Your legacy will endure forever.'
    }
  ],
  berserker: [
    {
      tier: 0,
      name: 'Squire',
      walked: 'You\'ve discovered the raw power within you. Your shots carry aggression; your style is bold. The court trembles with every stroke.',
      ahead: 'Harness that fury into consistency. Learn when to unleash and when to calculate. Pure power needs precision to dominate.'
    },
    {
      tier: 1,
      name: 'Knight',
      walked: 'Your opponents fear your attacks now. You\'ve proven that aggression, when controlled, is devastating. Your offensive arsenal is growing.',
      ahead: 'Master the rhythm of aggressive play. Learn to sustain pressure—one powerful shot leads to the next, relentlessly, until opponents crumble.'
    },
    {
      tier: 2,
      name: 'Champion',
      walked: 'You are fury incarnate on the court. Every point is a battle where you dominate through sheer offensive power. Opponents know: against you, they must survive.',
      ahead: 'Balance your aggression with tactical awareness. The greatest berserkers don\'t just attack—they orchestrate controlled chaos.'
    },
    {
      tier: 3,
      name: 'Warlord',
      walked: 'You have become the storm. Your attacks are relentless, overwhelming, inescapable. You reshape the game around your power. Opponents are swept away.',
      ahead: 'Few berserkers reach this pinnacle. Master the psychology of dominance. Break opponents\' wills, not just their defenses.'
    },
    {
      tier: 4,
      name: 'Conqueror',
      walked: 'You stand as the ultimate offensive force. Your power is legendary. Every shot is a declaration of superiority. The court bends to your will.',
      ahead: 'The summit calls. Become the unstoppable force that defines what berserker mastery truly means.'
    },
    {
      tier: 5,
      name: 'War God',
      walked: 'You are the embodiment of controlled fury—perfection in aggression. Your opponents face not a player, but a natural disaster. You are the standard by which all berserkers are measured.',
      ahead: 'You have reached the apex. Your legend is eternal.'
    }
  ],
  strategist: [
    {
      tier: 0,
      name: 'Squire',
      walked: 'You\'ve learned that the mind matters as much as the arm. Positioning, timing, court awareness—the building blocks of tactical mastery.',
      ahead: 'Begin orchestrating points. Move opponents where you want them. Let your mind control the rally, not chance or power.'
    },
    {
      tier: 1,
      name: 'Knight',
      walked: 'You\'ve proven that strategy wins rallies. Your opponents are confused, out of position, wondering what happened. Your game is becoming chess-like.',
      ahead: 'Deepen your tactical arsenal. Every shot is a calculated move. Begin anticipating two, three shots ahead.'
    },
    {
      tier: 2,
      name: 'Champion',
      walked: 'You control rallies through pure strategic brilliance. Opponents know they\'re being outthought, outmaneuvered. You don\'t just win—you dominate mentally.',
      ahead: 'Master the psychology of tempo. Control the pace, the rhythm, the flow. Make opponents play at your speed.'
    },
    {
      tier: 3,
      name: 'Warlord',
      walked: 'You are the calculating commander of the court. Every move is calculated three moves ahead. Opponents face not a player, but a thinking machine.',
      ahead: 'Few strategists reach true greatness. Master the art of adaptation—reshape your strategy on the fly based on opponent weakness.'
    },
    {
      tier: 4,
      name: 'Conqueror',
      walked: 'You have achieved strategic transcendence. Every rally is a puzzle you\'ve already solved. Your game is beautiful precision married to tactical superiority.',
      ahead: 'The final heights await. Perfect the synthesis of strategy, technique, and execution.'
    },
    {
      tier: 5,
      name: 'War God',
      walked: 'You are the ultimate strategist. Your opponents are not just beaten—they are schooled, taught, dismantled by your superior intellect. You are the grandmaster of the court.',
      ahead: 'You stand alone. Your legacy will teach generations the true meaning of strategic excellence.'
    }
  ],
  duelist: [
    {
      tier: 0,
      name: 'Squire',
      walked: 'You\'ve discovered balance. You attack when opportunity strikes and defend when required. You are becoming complete, dangerous in all situations.',
      ahead: 'Master the transitions. Switch between attack and defense flawlessly. Become an opponent\'s nightmare—never knowing what\'s coming next.'
    },
    {
      tier: 1,
      name: 'Knight',
      walked: 'You excel in all modes of play. Your opponents can\'t find weakness—you\'re strong both offensively and defensively. True versatility is your strength.',
      ahead: 'Synchronize your offense and defense. They should flow together seamlessly. Be unpredictable yet perfectly calculated.'
    },
    {
      tier: 2,
      name: 'Champion',
      walked: 'You are the complete player. Attack, defend, dictate—you master them all. Your versatility makes you nearly impossible to prepare for.',
      ahead: 'Elevate to the next level: perfect harmony. Your offense feeds your defense and vice versa. Become one with all aspects of the game.'
    },
    {
      tier: 3,
      name: 'Warlord',
      walked: 'You have achieved what few dare dream: true mastery across all dimensions of play. You are the complete warrior, equally dangerous in any situation.',
      ahead: 'Master the synthesis. At this level, the greatest duelists transcend categories—they simply play perfect tennis.'
    },
    {
      tier: 4,
      name: 'Conqueror',
      walked: 'You stand as the ultimate complete player. No weakness exists. You attack with precision, defend with brilliance, strategize with excellence. You are the standard.',
      ahead: 'The pinnacle awaits those brave enough to chase it. Perfect every aspect of your complete game.'
    },
    {
      tier: 5,
      name: 'War God',
      walked: 'You are the embodiment of complete mastery. You don\'t just play all aspects of the game—you elevate them to an art form. You are balanced perfection.',
      ahead: 'You have reached immortality. Your game will be studied and revered for generations.'
    }
  ],
  cross: [
    {
      tier: 0,
      name: 'Squire',
      walked: 'You walk an uncommon path—balancing all styles, all approaches. You\'re becoming a student of the full game. The court is your classroom.',
      ahead: 'Master the synthesis. Learn how each style complements the others. Become the player who understands every path.'
    },
    {
      tier: 1,
      name: 'Knight',
      walked: 'You are rare—a scholar of all techniques. While others specialize, you grow in all directions. Your unpredictability is your greatest weapon.',
      ahead: 'Harmonize your diverse skills. Let each aspect elevate the others. Become a complete threat from every position.'
    },
    {
      tier: 2,
      name: 'Champion',
      walked: 'You have transcended limitations. You attack like a berserker, defend like a guardian, strategize like a strategist, and balance like a duelist. You are wholly complete.',
      ahead: 'The next step is to make your diverse mastery look seamless. Let your opponents wonder which player they\'re facing.'
    },
    {
      tier: 3,
      name: 'Warlord',
      walked: 'You command the court with a sophistication only the rarest players achieve. You are not bound by category. You are the shapeshifter—adapting, evolving, dominating.',
      ahead: 'Few reach this apex. Master the art of perfect adaptation. Read opponents and become their worst nightmare.'
    },
    {
      tier: 4,
      name: 'Conqueror',
      walked: 'You stand alone at the intersection of all paths. Your mastery spans every style. You are the complete player—guardian, berserker, strategist, duelist, and more.',
      ahead: 'The summit awaits. Perfect the symphony of all techniques. Become the standard against which all balanced players are measured.'
    },
    {
      tier: 5,
      name: 'War God',
      walked: 'You are the ultimate expression of complete mastery—a living testament to the power of transcending limitations. You are perfect balance incarnate. Your legend bridges all schools.',
      ahead: 'You have reached the highest peak. Your legacy will define what it means to be truly, completely excellent.'
    }
  ]
};

// ─── IMAGE SETS PER CLASS × TIER ─────────────────────────────────────────────
const CLASS_IMAGES = {
  guardian: [
    { imageM: require('./assets/heroes/guardianm1.png'), imageF: require('./assets/heroes/guardianf1.png') },
    { imageM: require('./assets/heroes/guardianm2.png'), imageF: require('./assets/heroes/guardianf2.png') },
    { imageM: require('./assets/heroes/guardianm3.png'), imageF: require('./assets/heroes/guardianf3.png') },
    { imageM: require('./assets/heroes/guardianm4.png'), imageF: require('./assets/heroes/guardianf4.png') },
    { imageM: require('./assets/heroes/guardianm5.png'), imageF: require('./assets/heroes/guardianf5.png') },
    { imageM: require('./assets/heroes/guardianm5.png'), imageF: require('./assets/heroes/guardianf5.png') },
  ],
  berserker: [
    { imageM: require('./assets/heroes/berserkerm1.png'), imageF: require('./assets/heroes/berserkerf1.png') },
    { imageM: require('./assets/heroes/berserkerm2.png'), imageF: require('./assets/heroes/berserkerf2.png') },
    { imageM: require('./assets/heroes/berserkerm3.png'), imageF: require('./assets/heroes/berserkerf3.png') },
    { imageM: require('./assets/heroes/berserkerm4.png'), imageF: require('./assets/heroes/berserkerf4.png') },
    { imageM: require('./assets/heroes/berserkerm4.png'), imageF: require('./assets/heroes/berserkerf5.png') },
    { imageM: require('./assets/heroes/berserkerm4.png'), imageF: require('./assets/heroes/berserkerf5.png') },
  ],
  strategist: [
    { imageM: require('./assets/heroes/strategistm1.png'), imageF: require('./assets/heroes/strategistf1.png') },
    { imageM: require('./assets/heroes/strategistm2.png'), imageF: require('./assets/heroes/strategistf2.png') },
    { imageM: require('./assets/heroes/strategistm3.png'), imageF: require('./assets/heroes/strategistf3.png') },
    { imageM: require('./assets/heroes/strategistm4.png'), imageF: require('./assets/heroes/strategistf4.png') },
    { imageM: require('./assets/heroes/strategistm5.png'), imageF: require('./assets/heroes/strategistf5.png') },
    { imageM: require('./assets/heroes/strategistm5.png'), imageF: require('./assets/heroes/strategistf5.png') },
  ],
  duelist: [
    { imageM: require('./assets/heroes/duelistm1.png'), imageF: require('./assets/heroes/duelistf1.png') },
    { imageM: require('./assets/heroes/duelistm2.png'), imageF: require('./assets/heroes/duelistf2.png') },
    { imageM: require('./assets/heroes/duelistm3.png'), imageF: require('./assets/heroes/duelistf3.png') },
    { imageM: require('./assets/heroes/duelistm4.png'), imageF: require('./assets/heroes/duelistf4.png') },
    { imageM: require('./assets/heroes/duelistm5.png'), imageF: require('./assets/heroes/duelistf5.png') },
    { imageM: require('./assets/heroes/duelistm5.png'), imageF: require('./assets/heroes/duelistf5.png') },
  ],
};

// ─── BRANCH HELPERS ───────────────────────────────────────────────────────────
function getLeafIds(node) {
  if (!node || !node.children || node.children.length === 0) return node ? [node.id] : [];
  return node.children.flatMap(child => getLeafIds(child));
}

function findNode(id, nodes) {
  for (const node of nodes) {
    if (node.id === id) return node;
    if (node.children) {
      const found = findNode(id, node.children);
      if (found) return found;
    }
  }
  return null;
}

export function sumBranchPoints(branchNode, ratings) {
  if (!branchNode) return 0;
  return getLeafIds(branchNode).reduce((sum, id) => sum + (ratings[id] || 0), 0);
}

export function maxBranchPoints(branchNode) {
  if (!branchNode) return 0;
  return getLeafIds(branchNode).length * 3; // all maxRating = 3
}

function allLeavesAbove(branchNode, ratings, min) {
  if (!branchNode) return false;
  return getLeafIds(branchNode).every(id => (ratings[id] || 0) >= min);
}

function anyLeafAbove(branchNode, ratings, min) {
  if (!branchNode) return false;
  return getLeafIds(branchNode).some(id => (ratings[id] || 0) >= min);
}

export function getCoreBranches() {
  const tech = BRANCHES.find(b => b.id === 'technique');
  return {
    defense: tech?.children?.find(c => c.id === 'tech-defense'),
    attack: tech?.children?.find(c => c.id === 'tech-attack'),
    tactics: BRANCHES.find(b => b.id === 'tactics'),
    brawn: BRANCHES.find(b => b.id === 'brawn'),
  };
}

// ─── COURT POSITION INDICATOR ────────────────────────────────────────────────
// Determines if player plays right or left side based on skill distribution
export function computeCourtPosition(ratings, isLefty = false) {
  let rightPoints = 0, leftPoints = 0;

  // 1. Smash (flat overhead) vs Vibora (topspin overhead)
  const smash = findNode('tech-att-overhead-flat', BRANCHES);
  const vibora = findNode('tech-att-overhead-vibora', BRANCHES);
  const smashPts = sumBranchPoints(smash, ratings);
  const viboraPts = sumBranchPoints(vibora, ratings);
  if (smashPts > viboraPts) rightPoints += 1;
  else if (viboraPts > smashPts) leftPoints += 1;

  // 2. FH off-glass vs BH off-glass
  const fhOffGlass = findNode('tech-def-ofglass-fh', BRANCHES);
  const bhOffGlass = findNode('tech-def-ofglass-bh', BRANCHES);
  const fhOffPts = sumBranchPoints(fhOffGlass, ratings);
  const bhOffPts = sumBranchPoints(bhOffGlass, ratings);
  if (fhOffPts > bhOffPts) rightPoints += 1;
  else if (bhOffPts > fhOffPts) leftPoints += 1;

  // 3. Brawn: Speed vs Stamina (athletic attribute balance)
  // More Speed > Stamina = 1 right point
  const brawnBranch = BRANCHES.find(b => b.id === 'brawn');
  const brawnLeaves = getLeafIds(brawnBranch);
  const speedPts = brawnLeaves.filter(id => id.includes('speed')).reduce((sum, id) => sum + (ratings[id] || 0), 0);
  const staminaPts = brawnLeaves.filter(id => id.includes('stamina')).reduce((sum, id) => sum + (ratings[id] || 0), 0);
  if (speedPts > staminaPts) rightPoints += 1;
  else if (staminaPts > speedPts) leftPoints += 1;

  // Determine position
  let position = 'Neutral';
  if (rightPoints > leftPoints) position = 'Right';
  else if (leftPoints > rightPoints) position = 'Left';

  // Invert if lefty
  if (isLefty && position !== 'Neutral') {
    position = position === 'Right' ? 'Left' : 'Right';
  }

  return position;
}

// ─── ACHIEVEMENT DEFINITIONS ─────────────────────────────────────────────────
// Each achievement: { id, name, icon, desc, class, tier5, check(ratings) }
// tier5 = true marks the "80% mastery" class-specific pinnacle achievement

export const ACHIEVEMENTS = [

  // ── GUARDIAN ──────────────────────────────────────────────────────────────
  {
    id: 'guardian-ironclad',
    name: 'Ironclad',
    icon: '🧱',
    desc: '10 defense skills unlocked',
    class: 'guardian',
    tier: 1,
    check: (r) => {
      const { defense } = getCoreBranches();
      return defense ? getLeafIds(defense).filter(id => (r[id] || 0) > 0).length >= 10 : false;
    },
  },
  {
    id: 'guardian-iron-curtain',
    name: 'Iron Curtain',
    icon: '🛡️',
    desc: 'Every direct defense skill touched',
    class: 'guardian',
    tier: 1,
    check: (r) => {
      const node = findNode('tech-def-direct', BRANCHES);
      return node ? allLeavesAbove(node, r, 1) : false;
    },
  },
  {
    id: 'guardian-shattered-glass',
    name: 'Shattered Glass',
    icon: '🪟',
    desc: 'Every off-the-glass defense skill touched',
    class: 'guardian',
    tier: 1,
    check: (r) => {
      const node = findNode('tech-def-ofglass', BRANCHES);
      return node ? allLeavesAbove(node, r, 1) : false;
    },
  },
  {
    id: 'guardian-heavens-gate',
    name: "Heaven's Gate",
    icon: '🏔️',
    desc: 'All Full Lob & Fast Lob variations touched',
    class: 'guardian',
    tier: 1,
    check: (r) => {
      const lobIds = getLeafIds({ id: 'root', children: BRANCHES })
        .filter(id => id.includes('-lob-full-') || id.includes('-lob-fast-'));
      return lobIds.length > 0 && lobIds.every(id => (r[id] || 0) > 0);
    },
  },
  {
    id: 'guardian-titans-stand',
    name: "Titan's Stand",
    icon: '⛩️',
    desc: 'Defense points ≥ 2× attack points',
    class: 'guardian',
    tier: 3,
    check: (r) => {
      const { defense, attack } = getCoreBranches();
      const def = sumBranchPoints(defense, r);
      const off = sumBranchPoints(attack, r);
      return def > 0 && def >= off * 2;
    },
  },
  {
    id: 'guardian-mirrors-edge',
    name: "Mirror's Edge",
    icon: '🪞',
    desc: 'All Contrapared skills perfected',
    class: 'guardian',
    tier: 3,
    check: (r) => {
      const fhC = findNode('tech-def-direct-fh-contrapared', BRANCHES);
      const bhC = findNode('tech-def-direct-bh-contrapared', BRANCHES);
      return (fhC ? allLeavesAbove(fhC, r, 3) : false) &&
             (bhC ? allLeavesAbove(bhC, r, 3) : false);
    },
  },
  {
    id: 'guardian-aegis-eternal',
    name: 'Aegis Eternal',
    icon: '🏰',
    desc: '80% of all defense skills mastered',
    class: 'guardian',
    tier: 5,
    tier5: true,
    check: (r) => {
      const { defense } = getCoreBranches();
      if (!defense) return false;
      const max = maxBranchPoints(defense);
      return max > 0 && sumBranchPoints(defense, r) / max >= 0.8;
    },
  },
  {
    id: 'guardian-stone-colossus',
    name: 'Stone Colossus',
    icon: '🗿',
    desc: '25 defense skill leaves touched',
    class: 'guardian',
    tier: 2,
    check: (r) => {
      const { defense } = getCoreBranches();
      return defense ? getLeafIds(defense).filter(id => (r[id] || 0) > 0).length >= 25 : false;
    },
  },
  {
    id: 'guardian-the-bulwark',
    name: 'The Bulwark',
    icon: '🏯',
    desc: '100 defense points accumulated',
    class: 'guardian',
    tier: 2,
    check: (r) => {
      const { defense } = getCoreBranches();
      return sumBranchPoints(defense, r) >= 100;
    },
  },
  {
    id: 'guardian-eternal-rampart',
    name: 'Eternal Rampart',
    icon: '🌊',
    desc: 'Defense points ≥ 3× attack points',
    class: 'guardian',
    tier: 2,
    check: (r) => {
      const { defense, attack } = getCoreBranches();
      const def = sumBranchPoints(defense, r);
      const off = sumBranchPoints(attack, r);
      return def > 0 && def >= off * 3;
    },
  },
  {
    id: 'guardian-glacial-fortress',
    name: 'Glacial Fortress',
    icon: '🧊',
    desc: 'Every defense skill leaf touched at least once',
    class: 'guardian',
    tier: 2,
    check: (r) => {
      const { defense } = getCoreBranches();
      return defense ? allLeavesAbove(defense, r, 1) : false;
    },
  },
  {
    id: 'guardian-wardens-oath',
    name: "Warden's Oath",
    icon: '⛓️',
    desc: 'All 4 lob branches (FH/BH direct + FH/BH off-glass) mastered',
    class: 'guardian',
    tier: 2,
    check: (r) => {
      const nodes = [
        findNode('tech-def-direct-fh-lob',   BRANCHES),
        findNode('tech-def-direct-bh-lob',   BRANCHES),
        findNode('tech-def-ofglass-fh-lob',  BRANCHES),
        findNode('tech-def-ofglass-bh-lob',  BRANCHES),
      ];
      return nodes.every(n => n ? allLeavesAbove(n, r, 2) : false);
    },
  },
  {
    id: 'guardian-odins-shield',
    name: "Odin's Shield",
    icon: '🌟',
    desc: 'Both contrapared nodes and both off-glass slices perfected',
    class: 'guardian',
    tier: 5,
    check: (r) => {
      const nodes = [
        findNode('tech-def-direct-fh-contrapared', BRANCHES),
        findNode('tech-def-direct-bh-contrapared', BRANCHES),
        findNode('tech-def-ofglass-fh-slice',      BRANCHES),
        findNode('tech-def-ofglass-bh-slice',      BRANCHES),
      ];
      return nodes.every(n => n ? allLeavesAbove(n, r, 3) : false);
    },
  },
  {
    id: 'guardian-the-unbroken',
    name: 'The Unbroken',
    icon: '💪',
    desc: '15 defense skill leaves perfected (3/3)',
    class: 'guardian',
    tier: 2,
    check: (r) => {
      const { defense } = getCoreBranches();
      return defense ? getLeafIds(defense).filter(id => (r[id] || 0) === 3).length >= 15 : false;
    },
  },
  {
    id: 'guardian-bastion-of-ages',
    name: 'Bastion of Ages',
    icon: '🏰',
    desc: '200 defense points accumulated',
    class: 'guardian',
    tier: 3,
    check: (r) => {
      const { defense } = getCoreBranches();
      return sumBranchPoints(defense, r) >= 200;
    },
  },
  {
    id: 'guardian-godwall',
    name: 'Godwall',
    icon: '👁️',
    desc: 'All defense skills at 2/3 or above',
    class: 'guardian',
    tier: 3,
    check: (r) => {
      const { defense } = getCoreBranches();
      return defense ? allLeavesAbove(defense, r, 2) : false;
    },
  },
  {
    id: 'guardian-death-refuses-you',
    name: 'Death Refuses You',
    icon: '☠️',
    desc: 'Defense points ≥ 4× tactics points',
    class: 'guardian',
    tier: 4,
    check: (r) => {
      const { defense, tactics } = getCoreBranches();
      const def = sumBranchPoints(defense, r);
      const tac = sumBranchPoints(tactics, r);
      return def > 0 && def >= tac * 4;
    },
  },
  {
    id: 'guardian-the-attrition',
    name: 'The Attrition',
    icon: '⚔️',
    desc: 'Defense points exceed attack + tactics combined',
    class: 'guardian',
    tier: 3,
    check: (r) => {
      const { defense, attack, tactics } = getCoreBranches();
      const def = sumBranchPoints(defense, r);
      return def > 0 && def > sumBranchPoints(attack, r) + sumBranchPoints(tactics, r);
    },
  },
  {
    id: 'guardian-siege-survivor',
    name: 'Siege Survivor',
    icon: '🏯',
    desc: '150 defense points accumulated',
    class: 'guardian',
    tier: 3,
    check: (r) => {
      const { defense } = getCoreBranches();
      return sumBranchPoints(defense, r) >= 150;
    },
  },
  {
    id: 'guardian-war-of-patience',
    name: 'War of Patience',
    icon: '⏳',
    desc: 'All BH direct defense skills perfected',
    class: 'guardian',
    tier: 3,
    check: (r) => {
      const bh = findNode('tech-def-direct-bh', BRANCHES);
      return bh ? allLeavesAbove(bh, r, 3) : false;
    },
  },
  {
    id: 'guardian-scorched-earth',
    name: 'Scorched Earth',
    icon: '🔥',
    desc: 'All FH direct defense skills perfected',
    class: 'guardian',
    tier: 3,
    check: (r) => {
      const fh = findNode('tech-def-direct-fh', BRANCHES);
      return fh ? allLeavesAbove(fh, r, 3) : false;
    },
  },
  {
    id: 'guardian-sentinels-watch',
    name: "Sentinel's Watch",
    icon: '👁️',
    desc: 'Defense ≥ 50 pts and any tactics point scored',
    class: 'guardian',
    tier: 4,
    check: (r) => {
      const { defense, tactics } = getCoreBranches();
      return sumBranchPoints(defense, r) >= 50 && sumBranchPoints(tactics, r) > 0;
    },
  },
  {
    id: 'guardian-the-unassailable',
    name: 'The Unassailable',
    icon: '💀',
    desc: '250 defense points accumulated',
    class: 'guardian',
    tier: 4,
    check: (r) => {
      const { defense } = getCoreBranches();
      return sumBranchPoints(defense, r) >= 250;
    },
  },
  {
    id: 'guardian-heavens-barrage',
    name: "Heaven's Barrage",
    icon: '🌧️',
    desc: 'All 4 lob branches (FH/BH direct + off-glass) fully perfected',
    class: 'guardian',
    tier: 5,
    check: (r) => {
      const nodes = [
        findNode('tech-def-direct-fh-lob',  BRANCHES),
        findNode('tech-def-direct-bh-lob',  BRANCHES),
        findNode('tech-def-ofglass-fh-lob', BRANCHES),
        findNode('tech-def-ofglass-bh-lob', BRANCHES),
      ];
      return nodes.every(n => n ? allLeavesAbove(n, r, 3) : false);
    },
  },
  {
    id: 'guardian-the-rearguard',
    name: 'The Rearguard',
    icon: '🛡️',
    desc: 'All direct defense skills at 2/3 or above',
    class: 'guardian',
    tier: 3,
    check: (r) => {
      const direct = findNode('tech-def-direct', BRANCHES);
      return direct ? allLeavesAbove(direct, r, 2) : false;
    },
  },
  {
    id: 'guardian-ramparts-revenge',
    name: "Rampart's Revenge",
    icon: '⚡',
    desc: 'Both FH and BH direct lob branches fully perfected',
    class: 'guardian',
    tier: 5,
    check: (r) => {
      const fhLob = findNode('tech-def-direct-fh-lob', BRANCHES);
      const bhLob = findNode('tech-def-direct-bh-lob', BRANCHES);
      return (fhLob ? allLeavesAbove(fhLob, r, 3) : false) &&
             (bhLob ? allLeavesAbove(bhLob, r, 3) : false);
    },
  },
  {
    id: 'guardian-iron-resolve',
    name: 'Iron Resolve',
    icon: '⛓️',
    desc: 'All direct defense at 2/3+ and all off-glass skills touched',
    class: 'guardian',
    tier: 4,
    check: (r) => {
      const direct  = findNode('tech-def-direct',  BRANCHES);
      const ofglass = findNode('tech-def-ofglass', BRANCHES);
      return (direct  ? allLeavesAbove(direct,  r, 2) : false) &&
             (ofglass ? allLeavesAbove(ofglass, r, 1) : false);
    },
  },
  {
    id: 'guardian-the-last-guardian',
    name: 'The Last Guardian',
    icon: '🗡️',
    desc: '300 defense points accumulated',
    class: 'guardian',
    tier: 5,
    check: (r) => {
      const { defense } = getCoreBranches();
      return sumBranchPoints(defense, r) >= 300;
    },
  },
  {
    id: 'guardian-fortress-mind',
    name: 'Fortress Mind',
    icon: '🧠',
    desc: 'Defense ≥ 100 and all 4 defensive sub-branches touched',
    class: 'guardian',
    tier: 4,
    check: (r) => {
      const { defense } = getCoreBranches();
      if (sumBranchPoints(defense, r) < 100) return false;
      const subBranches = [
        findNode('tech-def-direct-fh',  BRANCHES),
        findNode('tech-def-direct-bh',  BRANCHES),
        findNode('tech-def-ofglass-fh', BRANCHES),
        findNode('tech-def-ofglass-bh', BRANCHES),
      ];
      return subBranches.every(n => n ? anyLeafAbove(n, r, 1) : false);
    },
  },
  {
    id: 'guardian-stone-skin',
    name: 'Stone Skin',
    icon: '🪨',
    desc: '30 defense skill leaves perfected (3/3)',
    class: 'guardian',
    tier: 4,
    check: (r) => {
      const { defense } = getCoreBranches();
      return defense ? getLeafIds(defense).filter(id => (r[id] || 0) === 3).length >= 30 : false;
    },
  },
  {
    id: 'guardian-mirror-of-war',
    name: 'Mirror of War',
    icon: '🪞',
    desc: 'All 4 slice nodes (FH/BH direct + off-glass) at 2/3+',
    class: 'guardian',
    tier: 5,
    check: (r) => {
      const nodes = [
        findNode('tech-def-direct-fh-slice',  BRANCHES),
        findNode('tech-def-direct-bh-slice',  BRANCHES),
        findNode('tech-def-ofglass-fh-slice', BRANCHES),
        findNode('tech-def-ofglass-bh-slice', BRANCHES),
      ];
      return nodes.every(n => n ? allLeavesAbove(n, r, 2) : false);
    },
  },
  {
    id: 'guardian-descent-master',
    name: 'Descent Master',
    icon: '⬇️',
    desc: 'FH and BH Bajada both fully perfected',
    class: 'guardian',
    tier: 5,
    check: (r) => {
      const fhB = findNode('tech-def-ofglass-fh-bajada', BRANCHES);
      const bhB = findNode('tech-def-ofglass-bh-bajada', BRANCHES);
      return (fhB ? allLeavesAbove(fhB, r, 3) : false) &&
             (bhB ? allLeavesAbove(bhB, r, 3) : false);
    },
  },

  // ── BERSERKER ─────────────────────────────────────────────────────────────
  {
    id: 'berserker-first-blood',
    name: 'First Blood',
    icon: '🩸',
    desc: 'First attack skill point scored',
    class: 'berserker',
    tier: 1,
    check: (r) => {
      const { attack } = getCoreBranches();
      return attack ?anyLeafAbove(attack, r, 1) : false;
    },
  },
  {
    id: 'berserker-blade-dance',
    name: 'Blade Dance',
    icon: '🏓',
    desc: 'All volley skills at 2/3 or above',
    class: 'berserker',
    tier: 2,
    check: (r) => {
      const fhV = findNode('tech-att-fh-volley', BRANCHES);
      const bhV = findNode('tech-att-bh-volley', BRANCHES);
      return (fhV ? allLeavesAbove(fhV, r, 2) : false) &&
             (bhV ? allLeavesAbove(bhV, r, 2) : false);
    },
  },
  {
    id: 'berserker-thunders-wrath',
    name: "Thunder's Wrath",
    icon: '💥',
    desc: 'All overhead variations unlocked',
    class: 'berserker',
    tier: 1,
    check: (r) => {
      const oh = findNode('tech-att-overhead', BRANCHES);
      return oh ? allLeavesAbove(oh, r, 1) : false;
    },
  },
  {
    id: 'berserker-vipers-fang',
    name: "Viper's Fang",
    icon: '🐍',
    desc: 'Vibora fully perfected',
    class: 'berserker',
    tier: 1,
    check: (r) => {
      const vib = findNode('tech-att-overhead-vibora', BRANCHES);
      return vib ? allLeavesAbove(vib, r, 3) : false;
    },
  },
  {
    id: 'berserker-berserk-rage',
    name: 'Berserk Rage',
    icon: '🔥',
    desc: 'Offense exceeds defense by 50+ points',
    class: 'berserker',
    tier: 3,
    check: (r) => {
      const { defense, attack } = getCoreBranches();
      return sumBranchPoints(attack, r) - sumBranchPoints(defense, r) >= 50;
    },
  },
  {
    id: 'berserker-mjolnirs-strike',
    name: "Mjolnir's Strike",
    icon: '☄️',
    desc: 'All Flat Smash variants perfected',
    class: 'berserker',
    tier: 2,
    check: (r) => {
      const fs = findNode('tech-att-overhead-flat', BRANCHES);
      return fs ? allLeavesAbove(fs, r, 3) : false;
    },
  },
  {
    id: 'berserker-fury-incarnate',
    name: 'Fury Incarnate',
    icon: '⚡',
    desc: '80% of all attack skills mastered',
    class: 'berserker',
    tier: 5,
    tier5: true,
    check: (r) => {
      const { attack } = getCoreBranches();
      if (!attack) return false;
      const max = maxBranchPoints(attack);
      return max > 0 && sumBranchPoints(attack, r) / max >= 0.8;
    },
  },
  {
    id: 'berserker-ragnarok',
    name: 'Ragnarök',
    icon: '💀',
    desc: '100 attack points accumulated',
    class: 'berserker',
    tier: 2,
    check: (r) => {
      const { attack } = getCoreBranches();
      return sumBranchPoints(attack, r) >= 100;
    },
  },
  {
    id: 'berserker-blood-frenzy',
    name: 'Blood Frenzy',
    icon: '🩸',
    desc: '25 attack skill leaves touched',
    class: 'berserker',
    tier: 1,
    check: (r) => {
      const { attack } = getCoreBranches();
      return attack ?getLeafIds(attack).filter(id => (r[id] || 0) > 0).length >= 25 : false;
    },
  },
  {
    id: 'berserker-deaths-waltz',
    name: "Death's Waltz",
    icon: '💃',
    desc: 'All FH and BH volley variants at 2/3 or above',
    class: 'berserker',
    tier: 2,
    check: (r) => {
      const fhV = findNode('tech-att-fh-volley', BRANCHES);
      const bhV = findNode('tech-att-bh-volley', BRANCHES);
      return (fhV ? allLeavesAbove(fhV, r, 2) : false) &&
             (bhV ? allLeavesAbove(bhV, r, 2) : false);
    },
  },
  {
    id: 'berserker-war-incarnate',
    name: 'War Incarnate',
    icon: '🔱',
    desc: 'Offense points ≥ 3× defense points',
    class: 'berserker',
    tier: 3,
    check: (r) => {
      const { defense, attack } = getCoreBranches();
      const def = sumBranchPoints(defense, r);
      const off = sumBranchPoints(attack, r);
      return off > 0 && off >= def * 3;
    },
  },
  {
    id: 'berserker-thors-hammer',
    name: "Thor's Hammer",
    icon: '🔨',
    desc: 'All flat overhead variants fully perfected',
    class: 'berserker',
    tier: 2,
    check: (r) => {
      const flat = findNode('tech-att-overhead-flat', BRANCHES);
      return flat ? allLeavesAbove(flat, r, 3) : false;
    },
  },
  {
    id: 'berserker-serpents-sebrawn-fang',
    name: "Serpent's Second Fang",
    icon: '🐍',
    desc: 'Both topspin overheads (Rulo + X3) fully perfected',
    class: 'berserker',
    tier: 3,
    check: (r) => {
      const rulo = findNode('tech-att-overhead-topspin-rulo', BRANCHES);
      const x3   = findNode('tech-att-overhead-topspin-x3',  BRANCHES);
      return (rulo ? allLeavesAbove(rulo, r, 3) : false) &&
             (x3   ? allLeavesAbove(x3,   r, 3) : false);
    },
  },
  {
    id: 'berserker-crimson-tempest',
    name: 'Crimson Tempest',
    icon: '🌀',
    desc: '200 attack points accumulated',
    class: 'berserker',
    tier: 4,
    check: (r) => {
      const { attack } = getCoreBranches();
      return sumBranchPoints(attack, r) >= 200;
    },
  },
  {
    id: 'berserker-warpath',
    name: 'Warpath',
    icon: '⚡',
    desc: 'Every attack skill leaf touched at least once',
    class: 'berserker',
    tier: 4,
    check: (r) => {
      const { attack } = getCoreBranches();
      return attack ?allLeavesAbove(attack, r, 1) : false;
    },
  },
  {
    id: 'berserker-berserkers-covenant',
    name: "Berserker's Covenant",
    icon: '🤝',
    desc: '20 attack skill leaves perfected (3/3)',
    class: 'berserker',
    tier: 4,
    check: (r) => {
      const { attack } = getCoreBranches();
      return attack ?getLeafIds(attack).filter(id => (r[id] || 0) === 3).length >= 20 : false;
    },
  },
  {
    id: 'berserker-slaughter-king',
    name: 'Slaughter King',
    icon: '💎',
    desc: 'All attack skills at 2/3 or above',
    class: 'berserker',
    tier: 3,
    check: (r) => {
      const { attack } = getCoreBranches();
      return attack ?allLeavesAbove(attack, r, 2) : false;
    },
  },
  {
    id: 'berserker-the-blitzkrieg',
    name: 'The Blitzkrieg',
    icon: '💨',
    desc: '150 attack points accumulated',
    class: 'berserker',
    tier: 3,
    check: (r) => {
      const { attack } = getCoreBranches();
      return sumBranchPoints(attack, r) >= 150;
    },
  },
  {
    id: 'berserker-no-quarter-given',
    name: 'No Quarter Given',
    icon: '🩸',
    desc: 'Offense points ≥ 4× defense points',
    class: 'berserker',
    tier: 4,
    check: (r) => {
      const { defense, attack } = getCoreBranches();
      const off = sumBranchPoints(attack, r);
      return off > 0 && off >= sumBranchPoints(defense, r) * 4;
    },
  },
  {
    id: 'berserker-the-killing-blow',
    name: 'The Killing Blow',
    icon: '💀',
    desc: '10 attack skill leaves perfected (3/3)',
    class: 'berserker',
    tier: 2,
    check: (r) => {
      const { attack } = getCoreBranches();
      return attack ?getLeafIds(attack).filter(id => (r[id] || 0) === 3).length >= 10 : false;
    },
  },
  {
    id: 'berserker-blood-siege',
    name: 'Blood Siege',
    icon: '🔥',
    desc: 'All overhead skill leaves at 2/3 or above',
    class: 'berserker',
    tier: 3,
    check: (r) => {
      const oh = findNode('tech-att-overhead', BRANCHES);
      return oh ? allLeavesAbove(oh, r, 2) : false;
    },
  },
  {
    id: 'berserker-apex-hunter',
    name: 'Apex Hunter',
    icon: '👑',
    desc: 'All FH and BH volley leaves fully perfected',
    class: 'berserker',
    tier: 4,
    check: (r) => {
      const fhV = findNode('tech-att-fh-volley', BRANCHES);
      const bhV = findNode('tech-att-bh-volley', BRANCHES);
      return (fhV ? allLeavesAbove(fhV, r, 3) : false) &&
             (bhV ? allLeavesAbove(bhV, r, 3) : false);
    },
  },
  {
    id: 'berserker-the-obliterator',
    name: 'The Obliterator',
    icon: '💎',
    desc: '250 attack points accumulated',
    class: 'berserker',
    tier: 4,
    check: (r) => {
      const { attack } = getCoreBranches();
      return sumBranchPoints(attack, r) >= 250;
    },
  },
  {
    id: 'berserker-wrath-incarnate',
    name: 'Wrath Incarnate',
    icon: '🌩️',
    desc: 'Offense points ≥ 3× tactics points',
    class: 'berserker',
    tier: 5,
    check: (r) => {
      const { attack, tactics } = getCoreBranches();
      const off = sumBranchPoints(attack, r);
      return off > 0 && off >= sumBranchPoints(tactics, r) * 3;
    },
  },
  {
    id: 'berserker-the-executioners-blade',
    name: "The Executioner's Blade",
    icon: '⚔️',
    desc: 'Vibora AND Gancho both fully perfected',
    class: 'berserker',
    tier: 5,
    check: (r) => {
      const vibora = findNode('tech-att-overhead-vibora',     BRANCHES);
      const gancho = findNode('tech-att-overhead-flat-gancho', BRANCHES);
      return (vibora ? allLeavesAbove(vibora, r, 3) : false) &&
             (gancho ? allLeavesAbove(gancho, r, 3) : false);
    },
  },
  {
    id: 'berserker-shock-assault',
    name: 'Shock Assault',
    icon: '🏹',
    desc: 'All overhead types touched and both volleys at 2/3+',
    class: 'berserker',
    tier: 4,
    check: (r) => {
      const oh  = findNode('tech-att-overhead',  BRANCHES);
      const fhV = findNode('tech-att-fh-volley', BRANCHES);
      const bhV = findNode('tech-att-bh-volley', BRANCHES);
      return (oh  ? allLeavesAbove(oh,  r, 1) : false) &&
             (fhV ? allLeavesAbove(fhV, r, 2) : false) &&
             (bhV ? allLeavesAbove(bhV, r, 2) : false);
    },
  },
  {
    id: 'berserker-relentless',
    name: 'Relentless',
    icon: '🔱',
    desc: 'Attack ≥ 200 pts and 15 attack leaves perfected',
    class: 'berserker',
    tier: 4,
    check: (r) => {
      const { attack } = getCoreBranches();
      if (!attack) return false;
      return sumBranchPoints(attack, r) >= 200 &&
             getLeafIds(attack).filter(id => (r[id] || 0) === 3).length >= 15;
    },
  },
  {
    id: 'berserker-blood-god',
    name: 'Blood God',
    icon: '🔴',
    desc: '300 attack points accumulated',
    class: 'berserker',
    tier: 5,
    check: (r) => {
      const { attack } = getCoreBranches();
      return sumBranchPoints(attack, r) >= 300;
    },
  },
  {
    id: 'berserker-lightning-reflex',
    name: 'Lightning Reflex',
    icon: '⚡',
    desc: 'All FH volley skills fully perfected',
    class: 'berserker',
    tier: 5,
    check: (r) => {
      const fhV = findNode('tech-att-fh-volley', BRANCHES);
      return fhV ? allLeavesAbove(fhV, r, 3) : false;
    },
  },
  {
    id: 'berserker-the-destroyer',
    name: 'The Destroyer',
    icon: '💥',
    desc: '30 attack skill leaves perfected (3/3)',
    class: 'berserker',
    tier: 5,
    check: (r) => {
      const { attack } = getCoreBranches();
      return attack ?getLeafIds(attack).filter(id => (r[id] || 0) === 3).length >= 30 : false;
    },
  },
  {
    id: 'berserker-iron-overhead',
    name: 'Iron Overhead',
    icon: '🔩',
    desc: 'All overhead types (flat, topspin, vibora) fully perfected',
    class: 'berserker',
    tier: 5,
    check: (r) => {
      const flat    = findNode('tech-att-overhead-flat',    BRANCHES);
      const topspin = findNode('tech-att-overhead-topspin', BRANCHES);
      const vibora  = findNode('tech-att-overhead-vibora',  BRANCHES);
      return (flat    ? allLeavesAbove(flat,    r, 3) : false) &&
             (topspin ? allLeavesAbove(topspin, r, 3) : false) &&
             (vibora  ? allLeavesAbove(vibora,  r, 3) : false);
    },
  },
  {
    id: 'berserker-topspin-arsenal',
    name: 'Topspin Arsenal',
    icon: '🌪️',
    desc: 'Rulo, X3 AND Vibora all fully perfected',
    class: 'berserker',
    tier: 5,
    check: (r) => {
      const rulo   = findNode('tech-att-overhead-topspin-rulo', BRANCHES);
      const x3     = findNode('tech-att-overhead-topspin-x3',  BRANCHES);
      const vibora = findNode('tech-att-overhead-vibora',      BRANCHES);
      return (rulo   ? allLeavesAbove(rulo,   r, 3) : false) &&
             (x3     ? allLeavesAbove(x3,     r, 3) : false) &&
             (vibora ? allLeavesAbove(vibora, r, 3) : false);
    },
  },

  // ── STRATEGIST ────────────────────────────────────────────────────────────
  {
    id: 'strategist-lokis-gambit',
    name: "Loki's Gambit",
    icon: '♟️',
    desc: 'First tactics skill point scored',
    class: 'strategist',
    tier: 1,
    check: (r) => {
      const { tactics } = getCoreBranches();
      return tactics ? anyLeafAbove(tactics, r, 1) : false;
    },
  },
  {
    id: 'strategist-third-eye',
    name: 'Third Eye',
    icon: '👁️',
    desc: '10 tactics skills unlocked',
    class: 'strategist',
    tier: 1,
    check: (r) => {
      const { tactics } = getCoreBranches();
      if (!tactics) return false;
      return getLeafIds(tactics).filter(id => (r[id] || 0) > 0).length >= 10;
    },
  },
  {
    id: 'strategist-lokis-disciple',
    name: "Loki's Disciple",
    icon: '🧩',
    desc: 'Tactics points exceed defense points',
    class: 'strategist',
    tier: 2,
    check: (r) => {
      const { defense, tactics } = getCoreBranches();
      const tac = sumBranchPoints(tactics, r);
      return tac > 0 && tac > sumBranchPoints(defense, r);
    },
  },
  {
    id: 'strategist-phantom-hand',
    name: 'Phantom Hand',
    icon: '🎭',
    desc: 'All tactics skills touched',
    class: 'strategist',
    tier: 1,
    check: (r) => {
      const { tactics } = getCoreBranches();
      return tactics ? allLeavesAbove(tactics, r, 1) : false;
    },
  },
  {
    id: 'strategist-war-council',
    name: 'War Council',
    icon: '📜',
    desc: '100 tactics points accumulated',
    class: 'strategist',
    tier: 2,
    check: (r) => {
      const { tactics } = getCoreBranches();
      return sumBranchPoints(tactics, r) >= 100;
    },
  },
  {
    id: 'strategist-mind-breaker',
    name: 'Mind Breaker',
    icon: '🧠',
    desc: 'Tactics points ≥ 2× attack points',
    class: 'strategist',
    tier: 2,
    check: (r) => {
      const { attack, tactics } = getCoreBranches();
      const tac = sumBranchPoints(tactics, r);
      return tac > 0 && tac >= sumBranchPoints(attack, r) * 2;
    },
  },
  {
    id: 'strategist-oracles-mind',
    name: "Oracle's Mind",
    icon: '🌟',
    desc: '80% of all tactics skills mastered',
    class: 'strategist',
    tier: 5,
    tier5: true,
    check: (r) => {
      const { tactics } = getCoreBranches();
      if (!tactics) return false;
      const max = maxBranchPoints(tactics);
      return max > 0 && sumBranchPoints(tactics, r) / max >= 0.8;
    },
  },
  {
    id: 'strategist-sage-of-ages',
    name: 'Sage of Ages',
    icon: '📚',
    desc: '50 tactics points accumulated',
    class: 'strategist',
    tier: 2,
    check: (r) => {
      const { tactics } = getCoreBranches();
      return sumBranchPoints(tactics, r) >= 50;
    },
  },
  {
    id: 'strategist-the-architect',
    name: 'The Architect',
    icon: '🏗️',
    desc: '20 tactics skill leaves touched',
    class: 'strategist',
    tier: 1,
    check: (r) => {
      const { tactics } = getCoreBranches();
      return tactics ? getLeafIds(tactics).filter(id => (r[id] || 0) > 0).length >= 20 : false;
    },
  },
  {
    id: 'strategist-veiled-sovereign',
    name: 'Veiled Sovereign',
    icon: '👁️',
    desc: 'Tactics points ≥ 3× defense points',
    class: 'strategist',
    tier: 3,
    check: (r) => {
      const { defense, tactics } = getCoreBranches();
      const tac = sumBranchPoints(tactics, r);
      return tac > 0 && tac >= sumBranchPoints(defense, r) * 3;
    },
  },
  {
    id: 'strategist-specters-hand',
    name: "Specter's Hand",
    icon: '👻',
    desc: 'All tactics skills at 2/3 or above',
    class: 'strategist',
    tier: 3,
    check: (r) => {
      const { tactics } = getCoreBranches();
      return tactics ? allLeavesAbove(tactics, r, 2) : false;
    },
  },
  {
    id: 'strategist-the-unseen-eye',
    name: 'The Unseen Eye',
    icon: '🎭',
    desc: 'Tactics points ≥ 3× attack points',
    class: 'strategist',
    tier: 3,
    check: (r) => {
      const { attack, tactics } = getCoreBranches();
      const tac = sumBranchPoints(tactics, r);
      return tac > 0 && tac >= sumBranchPoints(attack, r) * 3;
    },
  },
  {
    id: 'strategist-prophecy-fulfilled',
    name: 'Prophecy Fulfilled',
    icon: '⭐',
    desc: 'Every tactics skill leaf touched at least once',
    class: 'strategist',
    tier: 3,
    check: (r) => {
      const { tactics } = getCoreBranches();
      return tactics ? allLeavesAbove(tactics, r, 1) : false;
    },
  },
  {
    id: 'strategist-grand-architect',
    name: 'Grand Architect',
    icon: '🗺️',
    desc: '15 tactics skill leaves perfected (3/3)',
    class: 'strategist',
    tier: 2,
    check: (r) => {
      const { tactics } = getCoreBranches();
      return tactics ? getLeafIds(tactics).filter(id => (r[id] || 0) === 3).length >= 15 : false;
    },
  },
  {
    id: 'strategist-veil-of-eternity',
    name: 'Veil of Eternity',
    icon: '🌌',
    desc: '200 tactics points accumulated',
    class: 'strategist',
    tier: 3,
    check: (r) => {
      const { tactics } = getCoreBranches();
      return sumBranchPoints(tactics, r) >= 200;
    },
  },
  {
    id: 'strategist-the-unreadable',
    name: 'The Unreadable',
    icon: '🧿',
    desc: 'All tactics at 2/3+ AND 5 defense skills perfected',
    class: 'strategist',
    tier: 4,
    check: (r) => {
      const { defense, tactics } = getCoreBranches();
      const tacMastered = tactics ? allLeavesAbove(tactics, r, 2) : false;
      const defPerfected = defense ? getLeafIds(defense).filter(id => (r[id] || 0) === 3).length >= 5 : false;
      return tacMastered && defPerfected;
    },
  },
  {
    id: 'strategist-keeper-of-secrets',
    name: 'Keeper of Secrets',
    icon: '🗝️',
    desc: 'Tactics points ≥ 4× attack points',
    class: 'strategist',
    tier: 3,
    check: (r) => {
      const { attack, tactics } = getCoreBranches();
      const tac = sumBranchPoints(tactics, r);
      return tac > 0 && tac >= sumBranchPoints(attack, r) * 4;
    },
  },
  {
    id: 'strategist-the-flanker',
    name: 'The Flanker',
    icon: '🗺️',
    desc: '150 tactics points accumulated',
    class: 'strategist',
    tier: 2,
    check: (r) => {
      const { tactics } = getCoreBranches();
      return sumBranchPoints(tactics, r) >= 150;
    },
  },
  {
    id: 'strategist-psychological-warfare',
    name: 'Psychological Warfare',
    icon: '🧠',
    desc: 'Tactics points ≥ 4× defense points',
    class: 'strategist',
    tier: 4,
    check: (r) => {
      const { defense, tactics } = getCoreBranches();
      const tac = sumBranchPoints(tactics, r);
      return tac > 0 && tac >= sumBranchPoints(defense, r) * 4;
    },
  },
  {
    id: 'strategist-the-decoy',
    name: 'The Decoy',
    icon: '🪤',
    desc: 'Tactics + attack combined ≥ 100 points',
    class: 'strategist',
    tier: 3,
    check: (r) => {
      const { attack, tactics } = getCoreBranches();
      return sumBranchPoints(tactics, r) + sumBranchPoints(attack, r) >= 100;
    },
  },
  {
    id: 'strategist-the-flanking-maneuver',
    name: 'The Flanking Maneuver',
    icon: '🌀',
    desc: '250 tactics points accumulated',
    class: 'strategist',
    tier: 4,
    check: (r) => {
      const { tactics } = getCoreBranches();
      return sumBranchPoints(tactics, r) >= 250;
    },
  },
  {
    id: 'strategist-siege-mastermind',
    name: 'Siege Mastermind',
    icon: '🏰',
    desc: '20 tactics skill leaves perfected (3/3)',
    class: 'strategist',
    tier: 4,
    check: (r) => {
      const { tactics } = getCoreBranches();
      return tactics ? getLeafIds(tactics).filter(id => (r[id] || 0) === 3).length >= 20 : false;
    },
  },
  {
    id: 'strategist-the-invisible-chain',
    name: 'The Invisible Chain',
    icon: '⛓️',
    desc: 'Tactics ≥ 2× (attack + defense combined)',
    class: 'strategist',
    tier: 4,
    check: (r) => {
      const { defense, attack, tactics } = getCoreBranches();
      const tac = sumBranchPoints(tactics, r);
      return tac > 0 && tac >= (sumBranchPoints(attack, r) + sumBranchPoints(defense, r)) * 2;
    },
  },
  {
    id: 'strategist-shadow-commander',
    name: 'Shadow Commander',
    icon: '👤',
    desc: 'Tactics ≥ 150 pts and 10 attack leaves at 2/3+',
    class: 'strategist',
    tier: 4,
    check: (r) => {
      const { attack, tactics } = getCoreBranches();
      const tacPts   = sumBranchPoints(tactics, r);
      const offCount = attack ?getLeafIds(attack).filter(id => (r[id] || 0) >= 2).length : 0;
      return tacPts >= 150 && offCount >= 10;
    },
  },
  {
    id: 'strategist-the-war-room',
    name: 'The War Room',
    icon: '📊',
    desc: 'Tactics ≥ 100, defense ≥ 50, and attack ≥ 50 pts',
    class: 'strategist',
    tier: 4,
    check: (r) => {
      const { defense, attack, tactics } = getCoreBranches();
      return sumBranchPoints(tactics,  r) >= 100 &&
             sumBranchPoints(defense,  r) >= 50  &&
             sumBranchPoints(attack,  r) >= 50;
    },
  },
  {
    id: 'strategist-obsidian-mind',
    name: 'Obsidian Mind',
    icon: '🖤',
    desc: '25 tactics skill leaves perfected (3/3)',
    class: 'strategist',
    tier: 5,
    check: (r) => {
      const { tactics } = getCoreBranches();
      return tactics ? getLeafIds(tactics).filter(id => (r[id] || 0) === 3).length >= 25 : false;
    },
  },
  {
    id: 'strategist-the-grand-gambit',
    name: 'The Grand Gambit',
    icon: '♟️',
    desc: 'Tactics leads both attack and defense, all 3 branches ≥ 30 pts',
    class: 'strategist',
    tier: 5,
    check: (r) => {
      const { defense, attack, tactics } = getCoreBranches();
      const def = sumBranchPoints(defense,  r);
      const off = sumBranchPoints(attack,  r);
      const tac = sumBranchPoints(tactics,  r);
      return tac > off && tac > def && def >= 30 && off >= 30 && tac >= 30;
    },
  },
  {
    id: 'strategist-the-oracle',
    name: 'The Oracle',
    icon: '🌟',
    desc: '300 tactics points accumulated',
    class: 'strategist',
    tier: 5,
    check: (r) => {
      const { tactics } = getCoreBranches();
      return sumBranchPoints(tactics, r) >= 300;
    },
  },
  {
    id: 'strategist-absolute-control',
    name: 'Absolute Control',
    icon: '🎯',
    desc: 'Tactics points ≥ 5× defense points',
    class: 'strategist',
    tier: 5,
    check: (r) => {
      const { defense, tactics } = getCoreBranches();
      const tac = sumBranchPoints(tactics, r);
      return tac > 0 && tac >= sumBranchPoints(defense, r) * 5;
    },
  },
  {
    id: 'strategist-the-phantom-general',
    name: 'The Phantom General',
    icon: '👻',
    desc: '30 tactics skill leaves perfected (3/3)',
    class: 'strategist',
    tier: 5,
    check: (r) => {
      const { tactics } = getCoreBranches();
      return tactics ? getLeafIds(tactics).filter(id => (r[id] || 0) === 3).length >= 30 : false;
    },
  },
  {
    id: 'strategist-mind-over-matter',
    name: 'Mind Over Matter',
    icon: '🧿',
    desc: 'Tactics ≥ 250 pts and 20 tactics leaves perfected',
    class: 'strategist',
    tier: 5,
    check: (r) => {
      const { tactics } = getCoreBranches();
      if (!tactics) return false;
      return sumBranchPoints(tactics, r) >= 250 &&
             getLeafIds(tactics).filter(id => (r[id] || 0) === 3).length >= 20;
    },
  },
  {
    id: 'strategist-the-eternal-strategist',
    name: 'The Eternal Strategist',
    icon: '♾️',
    desc: 'Every defense and attack skill leaf touched at least once',
    class: 'strategist',
    tier: 5,
    check: (r) => {
      const { defense, attack } = getCoreBranches();
      return (defense ? allLeavesAbove(defense, r, 1) : false) &&
             (attack ?allLeavesAbove(attack, r, 1) : false);
    },
  },

  // ── DUELIST ───────────────────────────────────────────────────────────────
  {
    id: 'duelist-twin-fangs',
    name: 'Twin Fangs',
    icon: '⚔️',
    desc: 'Offense and tactics both reach 20+ points',
    class: 'duelist',
    tier: 1,
    check: (r) => {
      const { attack, tactics } = getCoreBranches();
      return sumBranchPoints(attack, r) >= 20 && sumBranchPoints(tactics, r) >= 20;
    },
  },
  {
    id: 'duelist-full-quiver',
    name: 'Full Quiver',
    icon: '🗡️',
    desc: 'Points scored in all 3 branches',
    class: 'duelist',
    tier: 1,
    check: (r) => {
      const { defense, attack, tactics } = getCoreBranches();
      return sumBranchPoints(defense, r) > 0 &&
             sumBranchPoints(attack, r) > 0 &&
             sumBranchPoints(tactics, r) > 0;
    },
  },
  {
    id: 'duelist-equilibrium',
    name: 'Equilibrium',
    icon: '⚖️',
    desc: 'Offense & tactics within 15% of each other, both ≥ 40 pts',
    class: 'duelist',
    tier: 2,
    check: (r) => {
      const { attack, tactics } = getCoreBranches();
      const off = sumBranchPoints(attack, r);
      const tac = sumBranchPoints(tactics, r);
      if (off < 40 || tac < 40) return false;
      const diff = Math.abs(off - tac);
      return diff / Math.max(off, tac) <= 0.15;
    },
  },
  {
    id: 'duelist-lightning-strike',
    name: 'Lightning Strike',
    icon: '🎯',
    desc: 'An overhead perfected + 5 tactics mastered',
    class: 'duelist',
    tier: 1,
    check: (r) => {
      const overheadTypes = [
        'tech-att-overhead-flat-gancho',
        'tech-att-overhead-flat-x4',
        'tech-att-overhead-flat-bounceback',
        'tech-att-overhead-topspin-rulo',
        'tech-att-overhead-topspin-x3',
        'tech-att-overhead-vibora',
      ];
      const overheadMastered = overheadTypes.some(nodeId => {
        const node = findNode(nodeId, BRANCHES);
        return node ? allLeavesAbove(node, r, 3) : false;
      });
      const { tactics } = getCoreBranches();
      const tacMastered = tactics ? getLeafIds(tactics).filter(id => (r[id] || 0) === 3).length >= 5 : false;
      return overheadMastered && tacMastered;
    },
  },
  {
    id: 'duelist-shadows-edge',
    name: "Shadow's Edge",
    icon: '🌑',
    desc: '80% of combined attack + tactics mastered',
    class: 'duelist',
    tier: 5,
    tier5: true,
    check: (r) => {
      const { attack, tactics } = getCoreBranches();
      if (!attack || !tactics) return false;
      const maxTotal = maxBranchPoints(attack) + maxBranchPoints(tactics);
      const actual   = sumBranchPoints(attack, r) + sumBranchPoints(tactics, r);
      return maxTotal > 0 && actual / maxTotal >= 0.8;
    },
  },
  {
    id: 'duelist-blades-promise',
    name: "Blade's Promise",
    icon: '⚔️',
    desc: 'Offense AND tactics each reach 30+ points',
    class: 'duelist',
    tier: 1,
    check: (r) => {
      const { attack, tactics } = getCoreBranches();
      return sumBranchPoints(attack, r) >= 30 && sumBranchPoints(tactics, r) >= 30;
    },
  },
  {
    id: 'duelist-the-convergence',
    name: 'The Convergence',
    icon: '🌀',
    desc: '10 attack + 10 tactics skill leaves touched',
    class: 'duelist',
    tier: 2,
    check: (r) => {
      const { attack, tactics } = getCoreBranches();
      const offTouched = attack ?getLeafIds(attack).filter(id => (r[id] || 0) > 0).length : 0;
      const tacTouched = tactics ? getLeafIds(tactics).filter(id => (r[id] || 0) > 0).length : 0;
      return offTouched >= 10 && tacTouched >= 10;
    },
  },
  {
    id: 'duelist-war-harmonist',
    name: 'War Harmonist',
    icon: '🎵',
    desc: 'Offense & tactics within 5% of each other (both ≥ 30)',
    class: 'duelist',
    tier: 2,
    check: (r) => {
      const { attack, tactics } = getCoreBranches();
      const off = sumBranchPoints(attack, r);
      const tac = sumBranchPoints(tactics, r);
      if (off < 30 || tac < 30) return false;
      return Math.abs(off - tac) / Math.max(off, tac) <= 0.05;
    },
  },
  {
    id: 'duelist-lethal-symphony',
    name: 'Lethal Symphony',
    icon: '🎼',
    desc: '5 attack + 5 tactics skill leaves perfected (3/3)',
    class: 'duelist',
    tier: 2,
    check: (r) => {
      const { attack, tactics } = getCoreBranches();
      const offPerf = attack ?getLeafIds(attack).filter(id => (r[id] || 0) === 3).length : 0;
      const tacPerf = tactics ? getLeafIds(tactics).filter(id => (r[id] || 0) === 3).length : 0;
      return offPerf >= 5 && tacPerf >= 5;
    },
  },
  {
    id: 'duelist-the-inevitable',
    name: 'The Inevitable',
    icon: '☄️',
    desc: 'Combined attack + tactics ≥ 100 points',
    class: 'duelist',
    tier: 3,
    check: (r) => {
      const { attack, tactics } = getCoreBranches();
      return sumBranchPoints(attack, r) + sumBranchPoints(tactics, r) >= 100;
    },
  },
  {
    id: 'duelist-eclipse-warrior',
    name: 'Eclipse Warrior',
    icon: '🌑',
    desc: 'One overhead type perfected + 15 tactics leaves touched',
    class: 'duelist',
    tier: 2,
    check: (r) => {
      const overheadTypes = [
        'tech-att-overhead-flat-gancho',
        'tech-att-overhead-flat-x4',
        'tech-att-overhead-flat-bounceback',
        'tech-att-overhead-topspin-rulo',
        'tech-att-overhead-topspin-x3',
        'tech-att-overhead-vibora',
      ];
      const overheadMastered = overheadTypes.some(nodeId => {
        const node = findNode(nodeId, BRANCHES);
        return node ? allLeavesAbove(node, r, 3) : false;
      });
      const { tactics } = getCoreBranches();
      const tacTouched = tactics ? getLeafIds(tactics).filter(id => (r[id] || 0) > 0).length : 0;
      return overheadMastered && tacTouched >= 15;
    },
  },
  {
    id: 'duelist-phantom-blade',
    name: 'Phantom Blade',
    icon: '👻',
    desc: 'All FH + BH volleys AND all tactics at 3/3',
    class: 'duelist',
    tier: 3,
    check: (r) => {
      const fhV = findNode('tech-att-fh-volley', BRANCHES);
      const bhV = findNode('tech-att-bh-volley', BRANCHES);
      const { tactics } = getCoreBranches();
      const volleysDone = (fhV ? allLeavesAbove(fhV, r, 3) : false) &&
                          (bhV ? allLeavesAbove(bhV, r, 3) : false);
      const tacPerf = tactics ? getLeafIds(tactics).filter(id => (r[id] || 0) === 3).length >= 3 : false;
      return volleysDone && tacPerf;
    },
  },
  {
    id: 'duelist-the-undying-duel',
    name: 'The Undying Duel',
    icon: '💀',
    desc: 'All 3 branches reach 20+ points',
    class: 'duelist',
    tier: 3,
    check: (r) => {
      const { defense, attack, tactics } = getCoreBranches();
      return sumBranchPoints(defense,  r) >= 20 &&
             sumBranchPoints(attack,  r) >= 20 &&
             sumBranchPoints(tactics,  r) >= 20;
    },
  },
  {
    id: 'duelist-master-of-all-arts',
    name: 'Master of All Arts',
    icon: '🏆',
    desc: '10 attack + 10 tactics skill leaves at 2/3 or above',
    class: 'duelist',
    tier: 3,
    check: (r) => {
      const { attack, tactics } = getCoreBranches();
      const offCount = attack ?getLeafIds(attack).filter(id => (r[id] || 0) >= 2).length : 0;
      const tacCount = tactics ? getLeafIds(tactics).filter(id => (r[id] || 0) >= 2).length : 0;
      return offCount >= 10 && tacCount >= 10;
    },
  },
  {
    id: 'duelist-storm-of-blades',
    name: 'Storm of Blades',
    icon: '⚡',
    desc: 'Combined attack + tactics ≥ 200 points',
    class: 'duelist',
    tier: 4,
    check: (r) => {
      const { attack, tactics } = getCoreBranches();
      return sumBranchPoints(attack, r) + sumBranchPoints(tactics, r) >= 200;
    },
  },
  {
    id: 'duelist-the-gladiators-code',
    name: "The Gladiator's Code",
    icon: '🏛️',
    desc: 'Offense ≥ 50 pts and tactics ≥ 50 pts',
    class: 'duelist',
    tier: 4,
    check: (r) => {
      const { attack, tactics } = getCoreBranches();
      return sumBranchPoints(attack, r) >= 50 && sumBranchPoints(tactics, r) >= 50;
    },
  },
  {
    id: 'duelist-two-sword-style',
    name: 'Two-Sword Style',
    icon: '🗡️',
    desc: '15 attack and 15 tactics skill leaves at 2/3+',
    class: 'duelist',
    tier: 3,
    check: (r) => {
      const { attack, tactics } = getCoreBranches();
      const offCount = attack ?getLeafIds(attack).filter(id => (r[id] || 0) >= 2).length : 0;
      const tacCount = tactics ? getLeafIds(tactics).filter(id => (r[id] || 0) >= 2).length : 0;
      return offCount >= 15 && tacCount >= 15;
    },
  },
  {
    id: 'duelist-the-ambush',
    name: 'The Ambush',
    icon: '🌑',
    desc: 'One overhead type perfected and 20 tactics leaves touched',
    class: 'duelist',
    tier: 3,
    check: (r) => {
      const overheadTypes = [
        'tech-att-overhead-flat-gancho', 'tech-att-overhead-flat-x4',
        'tech-att-overhead-flat-bounceback', 'tech-att-overhead-topspin-rulo',
        'tech-att-overhead-topspin-x3', 'tech-att-overhead-vibora',
      ];
      const overheadMastered = overheadTypes.some(id => {
        const n = findNode(id, BRANCHES);
        return n ? allLeavesAbove(n, r, 3) : false;
      });
      const { tactics } = getCoreBranches();
      const tacTouched = tactics ? getLeafIds(tactics).filter(id => (r[id] || 0) > 0).length : 0;
      return overheadMastered && tacTouched >= 20;
    },
  },
  {
    id: 'duelist-fluid-warrior',
    name: 'Fluid Warrior',
    icon: '🌊',
    desc: 'Offense ≥ 40, tactics ≥ 40, and defense ≥ 20 pts',
    class: 'duelist',
    tier: 2,
    check: (r) => {
      const { defense, attack, tactics } = getCoreBranches();
      return sumBranchPoints(attack,  r) >= 40 &&
             sumBranchPoints(tactics,  r) >= 40 &&
             sumBranchPoints(defense,  r) >= 20;
    },
  },
  {
    id: 'duelist-the-unpredictable',
    name: 'The Unpredictable',
    icon: '🎲',
    desc: 'Offense ≥ 60, tactics ≥ 60, both within 20% of each other',
    class: 'duelist',
    tier: 4,
    check: (r) => {
      const { attack, tactics } = getCoreBranches();
      const off = sumBranchPoints(attack, r);
      const tac = sumBranchPoints(tactics, r);
      if (off < 60 || tac < 60) return false;
      return Math.abs(off - tac) / Math.max(off, tac) <= 0.20;
    },
  },
  {
    id: 'duelist-dance-of-swords',
    name: 'Dance of Swords',
    icon: '💫',
    desc: 'All volleys at 2/3+ and all tactics at 2/3+',
    class: 'duelist',
    tier: 3,
    check: (r) => {
      const fhV = findNode('tech-att-fh-volley', BRANCHES);
      const bhV = findNode('tech-att-bh-volley', BRANCHES);
      const { tactics } = getCoreBranches();
      return (fhV     ? allLeavesAbove(fhV,     r, 2) : false) &&
             (bhV     ? allLeavesAbove(bhV,     r, 2) : false) &&
             (tactics ? allLeavesAbove(tactics, r, 2) : false);
    },
  },
  {
    id: 'duelist-the-roman-legion',
    name: 'The Roman Legion',
    icon: '🏛️',
    desc: 'All 3 branches reach 30+ points',
    class: 'duelist',
    tier: 4,
    check: (r) => {
      const { defense, attack, tactics } = getCoreBranches();
      return sumBranchPoints(defense,  r) >= 30 &&
             sumBranchPoints(attack,  r) >= 30 &&
             sumBranchPoints(tactics,  r) >= 30;
    },
  },
  {
    id: 'duelist-ronins-path',
    name: "Rōnin's Path",
    icon: '🎌',
    desc: 'Offense ≥ 80 pts and tactics ≥ 80 pts',
    class: 'duelist',
    tier: 4,
    check: (r) => {
      const { attack, tactics } = getCoreBranches();
      return sumBranchPoints(attack, r) >= 80 && sumBranchPoints(tactics, r) >= 80;
    },
  },
  {
    id: 'duelist-the-perfect-duel',
    name: 'The Perfect Duel',
    icon: '🌟',
    desc: 'Offense ≥ 100 pts and tactics ≥ 100 pts',
    class: 'duelist',
    tier: 5,
    check: (r) => {
      const { attack, tactics } = getCoreBranches();
      return sumBranchPoints(attack, r) >= 100 && sumBranchPoints(tactics, r) >= 100;
    },
  },
  {
    id: 'duelist-apex-of-war',
    name: 'Apex of War',
    icon: '🔱',
    desc: 'Combined attack + tactics ≥ 250 points',
    class: 'duelist',
    tier: 5,
    check: (r) => {
      const { attack, tactics } = getCoreBranches();
      return sumBranchPoints(attack, r) + sumBranchPoints(tactics, r) >= 250;
    },
  },
  {
    id: 'duelist-the-apex-gladiator',
    name: 'The Apex Gladiator',
    icon: '🏛️',
    desc: 'Combined attack + tactics ≥ 300 points',
    class: 'duelist',
    tier: 5,
    check: (r) => {
      const { attack, tactics } = getCoreBranches();
      return sumBranchPoints(attack, r) + sumBranchPoints(tactics, r) >= 300;
    },
  },
  {
    id: 'duelist-perfect-balance',
    name: 'Perfect Balance',
    icon: '⚖️',
    desc: 'Offense and tactics within 3% of each other, both ≥ 80 pts',
    class: 'duelist',
    tier: 5,
    check: (r) => {
      const { attack, tactics } = getCoreBranches();
      const off = sumBranchPoints(attack, r);
      const tac = sumBranchPoints(tactics, r);
      if (off < 80 || tac < 80) return false;
      return Math.abs(off - tac) / Math.max(off, tac) <= 0.03;
    },
  },
  {
    id: 'duelist-the-final-duel',
    name: 'The Final Duel',
    icon: '⚔️',
    desc: 'Offense ≥ 150 pts and tactics ≥ 150 pts',
    class: 'duelist',
    tier: 5,
    check: (r) => {
      const { attack, tactics } = getCoreBranches();
      return sumBranchPoints(attack, r) >= 150 && sumBranchPoints(tactics, r) >= 150;
    },
  },
  {
    id: 'duelist-war-artist',
    name: 'War Artist',
    icon: '🎨',
    desc: '20 attack and 20 tactics skill leaves at 2/3+',
    class: 'duelist',
    tier: 5,
    check: (r) => {
      const { attack, tactics } = getCoreBranches();
      const offCount = attack ?getLeafIds(attack).filter(id => (r[id] || 0) >= 2).length : 0;
      const tacCount = tactics ? getLeafIds(tactics).filter(id => (r[id] || 0) >= 2).length : 0;
      return offCount >= 20 && tacCount >= 20;
    },
  },
  {
    id: 'duelist-the-legend',
    name: 'The Legend',
    icon: '🌟',
    desc: 'All 3 branches reach 50+ points',
    class: 'duelist',
    tier: 5,
    check: (r) => {
      const { defense, attack, tactics } = getCoreBranches();
      return sumBranchPoints(defense,  r) >= 50 &&
             sumBranchPoints(attack,  r) >= 50 &&
             sumBranchPoints(tactics,  r) >= 50;
    },
  },

  // ── CROSS-CLASS ───────────────────────────────────────────────────────────
  {
    id: 'cross-blood-oath',
    name: 'Blood Oath',
    icon: '🌄',
    desc: 'First skill point ever scored',
    class: 'cross',
    tier: 1,
    check: (r) => Object.values(r).some(v => v > 0),
  },
  {
    id: 'cross-phoenix-reborn',
    name: 'Phoenix Reborn',
    icon: '🔥',
    desc: 'Class evolved for the first time',
    class: 'cross',
    tier: 4,
    // Checked externally (evolution animation) — always false in pure computation
    check: () => false,
  },
  {
    id: 'cross-warlords-balance',
    name: "Warlord's Balance",
    icon: '🎪',
    desc: 'At least 5 points in each of the 3 branches',
    class: 'cross',
    tier: 2,
    check: (r) => {
      const { defense, attack, tactics } = getCoreBranches();
      return sumBranchPoints(defense, r) >= 5 &&
             sumBranchPoints(attack, r) >= 5 &&
             sumBranchPoints(tactics, r) >= 5;
    },
  },
  {
    id: 'cross-flawless-blade',
    name: 'Flawless Blade',
    icon: '💎',
    desc: '10 skills fully perfected (3/3)',
    class: 'cross',
    tier: 3,
    check: (r) => Object.values(r).filter(v => v === 3).length >= 10,
  },
  {
    id: 'cross-immortal',
    name: 'Immortal',
    icon: '🏆',
    desc: 'Reached War God tier (201+ points)',
    class: 'cross',
    tier: 4,
    check: (r) => Object.values(r).reduce((s, v) => s + v, 0) >= 201,
  },
];

// ─── ACHIEVEMENT LOOKUP MAP ───────────────────────────────────────────────────
const ACHIEVEMENT_MAP = Object.fromEntries(ACHIEVEMENTS.map(a => [a.id, a]));
export function getAchievementDef(id) {
  return ACHIEVEMENT_MAP[id] || null;
}

// ─── COMPUTE UNLOCKED IDs FROM RATINGS ───────────────────────────────────────
// Pass { evolutionTriggered: true } when class evolution was just detected
// to also unlock 'cross-phoenix-reborn'.
export function computeUnlockedIds(ratings, options = {}) {
  const ids = ACHIEVEMENTS
    .filter(a => a.id !== 'cross-phoenix-reborn' && a.check(ratings))
    .map(a => a.id);
  if (options.evolutionTriggered) ids.push('cross-phoenix-reborn');
  return ids;
}

// ─── CLASS DETERMINATION ─────────────────────────────────────────────────────
// Tunable thresholds - see the reasoning below each one. If real-usage data
// later shows the class mix still looks off, adjust these first before
// touching the decision logic itself.
const CLASS_THRESHOLDS = {
  // A branch's raw percent is only trusted once this fraction of its leaf
  // skills has actually been rated. Below that, the percent is shrunk
  // proportionally, so e.g. 2 leaves rated 3/3 (a coincidental "100%") can't
  // outweigh a branch that's been broadly and honestly assessed.
  COVERAGE_FLOOR: 0.25,
  // Attack/defense are "close enough" to call Duelist within this margin.
  // Tightened from the old 0.15 now that COVERAGE_FLOOR already filters out
  // most sparse-data false positives, so this only has to catch genuine
  // near-ties.
  CLOSE_THRESHOLD: 0.12,
  // Both attack AND defense must clear this to be called Duelist - "equally
  // deadly on both sides" implies actual competence, not just similarity.
  // This is what stops every barely-rated player from defaulting to
  // Duelist (0% is "close to" 0%, but neither is remotely deadly).
  DUELIST_FLOOR: 0.30,
  // Tactics must lead BOTH other branches by this margin to win Strategist -
  // a real lead, not a coin-flip edge.
  STRATEGIST_MARGIN: 0.10,
  // Fraction of technique (defense + attack combined) leaves that must be
  // rated before Strategist is reachable at all. A player whose shots have
  // barely been assessed shouldn't be crowned Strategist off tactics alone.
  TECHNIQUE_GATE: 0.20,
};

function getBranchStats(branchNode, ratings) {
  const leafIds = getLeafIds(branchNode);
  const points = leafIds.reduce((sum, id) => sum + (ratings[id] || 0), 0);
  const max = leafIds.length * 3;
  // A leaf only appears as a key in `ratings` once a coach has actually
  // rated it (0 ratings are deleted server-side, never stored as 0) - so
  // key-membership is a genuine "was this assessed at all" signal, distinct
  // from the 0-3 score itself.
  const ratedCount = leafIds.filter(id => ratings[id] !== undefined).length;
  return {
    percent: max > 0 ? points / max : 0,
    coverage: leafIds.length > 0 ? ratedCount / leafIds.length : 0,
  };
}

// Shrinks a branch's percent toward 0 as its coverage falls short of
// COVERAGE_FLOOR, so a thinly-rated branch can't spike to an extreme
// percent off a couple of lucky ratings. Once coverage clears the floor,
// the raw percent passes through unchanged.
function confidenceAdjusted(stats, thresholds) {
  return stats.percent * Math.min(1, stats.coverage / thresholds.COVERAGE_FLOOR);
}

export function getHeroClass(ratings, thresholds = CLASS_THRESHOLDS) {
  const { defense, attack, tactics } = getCoreBranches();

  const defStats = getBranchStats(defense, ratings);
  const attStats = getBranchStats(attack, ratings);
  const tacStats = getBranchStats(tactics, ratings);

  const def = confidenceAdjusted(defStats, thresholds);
  const att = confidenceAdjusted(attStats, thresholds);
  const tac = confidenceAdjusted(tacStats, thresholds);

  const techLeafIds = [...getLeafIds(defense), ...getLeafIds(attack)];
  const ratedTechLeaves = techLeafIds.filter(id => ratings[id] !== undefined).length;
  const techCoverage = techLeafIds.length > 0 ? ratedTechLeaves / techLeafIds.length : 0;

  // 1. Strategist - checked first (tactics no longer structurally loses to
  // the duelist check below), but gated on having real technique data so a
  // player who's only ever had tactics rated can't get crowned off that
  // alone. Needs a genuine lead over BOTH other branches, not just being
  // nominally highest.
  const strategistEligible = techCoverage >= thresholds.TECHNIQUE_GATE;
  if (
    strategistEligible &&
    tac >= def + thresholds.STRATEGIST_MARGIN &&
    tac >= att + thresholds.STRATEGIST_MARGIN
  ) {
    return 'strategist';
  }

  // 2. Duelist - attack and defense are close AND both are genuinely
  // competent (not just similarly unrated).
  if (
    Math.abs(att - def) < thresholds.CLOSE_THRESHOLD &&
    att >= thresholds.DUELIST_FLOOR &&
    def >= thresholds.DUELIST_FLOOR
  ) {
    return 'duelist';
  }

  // 3. Fallback - whichever of defense/attack leads. Tactics is excluded
  // here too (not just from step 1) so the technique gate can't be
  // sidestepped through this branch.
  return att > def ? 'berserker' : 'guardian';
}

// ─── WEBP SUPPORT DETECTION ──────────────────────────────────────────────────
let webpSupported = null;

export function isWebPSupported() {
  // Cache result to avoid repeated checks
  if (webpSupported !== null) return webpSupported;

  try {
    const canvas = typeof document !== 'undefined' ? document.createElement('canvas') : null;
    if (!canvas) return false;

    webpSupported = canvas.toDataURL('image/webp').indexOf('data:image/webp') === 0;
  } catch (e) {
    webpSupported = false;
  }

  return webpSupported;
}

// ─── IMAGE RESOLVER WITH LAZY LOADING ────────────────────────────────────────
export function getHeroImage(heroClass, tierIndex, gender) {
  const classImages = CLASS_IMAGES[heroClass] || CLASS_IMAGES.guardian;
  const tierImages  = classImages[tierIndex]  || classImages[classImages.length - 1];
  const image = gender === 'f' && tierImages.imageF ? tierImages.imageF : tierImages.imageM;

  // Return the image as-is (string path from require)
  return image;
}

/**
 * Load hero image with WebP support and lazy loading
 * Returns URL with automatic format detection and fallback
 */
export function getHeroImageUrl(heroClass, tierIndex, gender) {
  const classImages = CLASS_IMAGES[heroClass] || CLASS_IMAGES.guardian;
  const tierImages  = classImages[tierIndex]  || classImages[classImages.length - 1];
  const baseImage = gender === 'f' && tierImages.imageF ? tierImages.imageF : tierImages.imageM;

  // Extract filename from require path
  let filename = '';
  if (typeof baseImage === 'string') {
    filename = baseImage.split('/').pop();
  } else if (baseImage.default) {
    filename = baseImage.default.split('/').pop();
  }

  // Return WebP if supported, fallback to PNG
  if (isWebPSupported()) {
    return filename.replace('.png', '.webp');
  }
  return filename;
}

// ─── TIER HELPERS ────────────────────────────────────────────────────────────
export function getHeroTier(totalPoints) {
  return HERO_TIERS.find(t => totalPoints >= t.minPoints && totalPoints <= t.maxPoints) || HERO_TIERS[0];
}

export function getNextHeroTier(totalPoints) {
  const current = getHeroTier(totalPoints);
  if (current.tier === HERO_TIERS.length - 1) return null;
  return HERO_TIERS[current.tier + 1];
}

export function getProgressToNextTier(totalPoints) {
  const current = getHeroTier(totalPoints);
  const next    = getNextHeroTier(totalPoints);
  if (!next) return 100;
  return Math.min(((totalPoints - current.minPoints) / (next.minPoints - current.minPoints)) * 100, 100);
}

// ─── PLAYTOMIC RATING SYSTEM (0-7 EQUIVALENCE) ───────────────────────────────
/**
 * Calculate Playtomic rating (0-6) based on skill point distribution.
 *
 * RATING THRESHOLDS (Updated):
 * 0    = < 12 total skill points
 * 0.5  = ≥ 1 point on EACH defensive skill (all defenses touched)
 * 1    = ≥ 70% defense + ALL attack skills have ≥ 1 point
 * 1.5  = ≥ 50% of tactics skills have ≥ 1 point
 * 2    = Total points ≥ 70 AND 50% of technique skills have ≥ 1 point
 * 2.5  = ≥ 100 total points AND 70% of technique skills have ≥ 1 point
 * 3    = ≥ 150 total points AND ≥ 20% of all skills at 2+ points
 * 3.5  = ≥ 200 total points AND ≥ 33% of all skills at 2+ points
 * 4    = ≥ 250 total points AND ≥ 66% of all skills at 2+ points
 * 4.5  = ≥ 300 total points AND ≥ 25% of skills at 3 points (maxed)
 * 5    = ≥ 350 total points AND ≥ 50% of skills at 3 points
 * 5.5  = ≥ 400 total points AND ≥ 75% of skills at 3 points
 * 6    = Total points = maximum possible skill points
 */
export function calculatePlaytomicRating(ratings) {
  const { defense, attack, tactics } = getCoreBranches();
  const technique = BRANCHES.find(b => b.id === 'technique');

  // Collect all leaf skill IDs from main branches
  const defenseLeaves = getLeafIds(defense);
  const attackLeaves = getLeafIds(attack);
  const tacticsLeaves = getLeafIds(tactics);
  const techniqueLeaves = getLeafIds(technique);
  const allLeaves = [...defenseLeaves, ...attackLeaves, ...tacticsLeaves];

  // Calculate total points across all skills
  const totalPoints = Object.values(ratings).reduce((sum, v) => sum + (v || 0), 0);

  // Start at base rating 0
  let rating = 0;

  // ── Check Rating 0.5 ────────────────────────────────────────────────────────
  // >= 12 total points
  if (totalPoints >= 12) {
    rating = 0.5;
  }

  // ── Check Rating 1.0 ────────────────────────────────────────────────────────
  // >= 25 total points
  if (totalPoints >= 25) {
    rating = 1.0;
  }

  // ── Check Rating 1.5 ────────────────────────────────────────────────────────
  // >= 50 total points
  if (totalPoints >= 50) {
    rating = 1.5;
  }

  // ── Check Rating 2.0 ────────────────────────────────────────────────────────
  // >= 70 total points
  if (totalPoints >= 70) {
    rating = 2.0;
  }

  // ── Check Rating 2.5 ────────────────────────────────────────────────────────
  // >= 100 total points
  if (totalPoints >= 100) {
    rating = 2.5;
  }

  // ── Check Rating 3.0 ────────────────────────────────────────────────────────
  // >= 150 total points
  if (totalPoints >= 150) {
    rating = 3.0;
  }

  // ── Check Rating 3.5 ────────────────────────────────────────────────────────
  // >= 200 total points AND >= 20% of all skills at 2+ points
  if (totalPoints >= 200) {
    const skillsWith2Plus = allLeaves.filter(id => (ratings[id] || 0) >= 2).length;
    const percentAt2Plus = skillsWith2Plus / allLeaves.length;
    if (percentAt2Plus >= 0.20) {
      rating = 3.5;
    }
  }

  // ── Check Rating 4.0 ────────────────────────────────────────────────────────
  // >= 250 total points AND >= 50% of all skills at 2+ points
  if (totalPoints >= 250) {
    const skillsWith2Plus = allLeaves.filter(id => (ratings[id] || 0) >= 2).length;
    const percentAt2Plus = skillsWith2Plus / allLeaves.length;
    if (percentAt2Plus >= 0.50) {
      rating = 4.0;
    }
  }

  // ── Check Rating 4.5 ────────────────────────────────────────────────────────
  // >= 300 total points AND >= 75% of all skills at 2+ points
  if (totalPoints >= 300) {
    const skillsWith2Plus = allLeaves.filter(id => (ratings[id] || 0) >= 2).length;
    const percentAt2Plus = skillsWith2Plus / allLeaves.length;
    if (percentAt2Plus >= 0.75) {
      rating = 4.5;
    }
  }

  // ── Check Rating 5.0 ────────────────────────────────────────────────────────
  // >= 350 total points AND >= 25% of all skills at 3 points (maxed)
  if (totalPoints >= 350) {
    const skillsWith3 = allLeaves.filter(id => (ratings[id] || 0) >= 3).length;
    const percentAt3 = skillsWith3 / allLeaves.length;
    if (percentAt3 >= 0.25) {
      rating = 5.0;
    }
  }

  // ── Check Rating 5.5 ────────────────────────────────────────────────────────
  // >= 400 total points AND >= 50% of all skills at 3 points
  if (totalPoints >= 400) {
    const skillsWith3 = allLeaves.filter(id => (ratings[id] || 0) >= 3).length;
    const percentAt3 = skillsWith3 / allLeaves.length;
    if (percentAt3 >= 0.50) {
      rating = 5.5;
    }
  }

  // ── Check Rating 6.0 ────────────────────────────────────────────────────────
  // All skills maxed (100% at 3 points)
  const maxPossiblePoints = allLeaves.length * 3;
  if (totalPoints === maxPossiblePoints) {
    rating = 6.0;
  }

  return rating;
}

/**
 * Calculate smooth Playtomic rating with accurate progress tracking.
 *
 * Takes into account:
 * - Percentage of skillpoints left to hit next threshold
 * - Percentage of completion for each secondary condition
 * - Weighted average based on how close to each requirement
 *
 * Returns a value like 2.35 showing accurate progress between 2.0 and 2.5
 */
export function calculatePlaytomicRatingSmooth(ratings) {
  // Start with the discrete rating
  const discreteRating = calculatePlaytomicRating(ratings);

  // Get all leaves and totals
  const { defense, attack, tactics } = getCoreBranches();
  const technique = BRANCHES.find(b => b.id === 'technique');
  const defenseLeaves = getLeafIds(defense);
  const attackLeaves = getLeafIds(attack);
  const tacticsLeaves = getLeafIds(tactics);
  const techniqueLeaves = getLeafIds(technique);
  const allLeaves = [...defenseLeaves, ...attackLeaves, ...tacticsLeaves];

  const totalPoints = Object.values(ratings).reduce((sum, v) => sum + (v || 0), 0);
  const maxPossible = allLeaves.length * 3;

  // Helper: calculate percentages
  const getSkillsAt2PlusPercent = () => allLeaves.filter(id => (ratings[id] || 0) >= 2).length / allLeaves.length;
  const getSkillsAt3Percent = () => allLeaves.filter(id => (ratings[id] || 0) >= 3).length / allLeaves.length;

  // If already at max, return 6.0
  if (discreteRating === 6.0) return 6.0;

  /**
   * Calculate accurate progress between two tiers.
   * Takes current/next values and requirements.
   * Returns progress 0-1 weighted by distance to each requirement.
   */
  const calculateProgressBetweenTiers = (
    currentValue1, nextRequirement1,
    currentValue2, nextRequirement2,
    currentRequirement2 = 0
  ) => {
    // Clamp values to 0-1 range
    currentValue1 = Math.max(0, Math.min(1, currentValue1));
    currentValue2 = Math.max(0, Math.min(1, currentValue2));

    // Calculate how far we are from meeting each requirement
    const progress1 = (currentValue1 - 0) / (nextRequirement1 - 0);
    const progress2 = (currentValue2 - currentRequirement2) / (nextRequirement2 - currentRequirement2);

    // Clamp to 0-1 range
    const clampedProgress1 = Math.max(0, Math.min(1, progress1));
    const clampedProgress2 = Math.max(0, Math.min(1, progress2));

    // Weight by how close we are to each requirement
    // If we're far from one requirement, weight the other more heavily
    const weight1 = clampedProgress1 >= clampedProgress2 ? 0.6 : 0.4;
    const weight2 = 1 - weight1;

    return clampedProgress1 * weight1 + clampedProgress2 * weight2;
  };

  let progressToNext = 0;

  // NEW SIMPLIFIED TIER SYSTEM - Points only until 3.0
  if (discreteRating === 0) {
    // Progress from 0 to 0.5: 0 pts → 12 pts
    progressToNext = totalPoints / 12;
  }
  else if (discreteRating === 0.5) {
    // 0.5 → 1: 12 pts → 25 pts (13 more points)
    progressToNext = (totalPoints - 12) / 13;
  }
  else if (discreteRating === 1.0) {
    // 1 → 1.5: 25 pts → 50 pts (25 more points)
    progressToNext = (totalPoints - 25) / 25;
  }
  else if (discreteRating === 1.5) {
    // 1.5 → 2: 50 pts → 70 pts (20 more points)
    progressToNext = (totalPoints - 50) / 20;
  }
  else if (discreteRating === 2.0) {
    // 2 → 2.5: 70 pts → 100 pts (30 more points)
    progressToNext = (totalPoints - 70) / 30;
  }
  else if (discreteRating === 2.5) {
    // 2.5 → 3: 100 pts → 150 pts (50 more points)
    progressToNext = (totalPoints - 100) / 50;
  }
  else if (discreteRating === 3.0) {
    // 3 → 3.5: 150 pts → 200 pts + 20% skills at 2+
    progressToNext = calculateProgressBetweenTiers(
      (totalPoints - 150) / 50, 1.0,
      getSkillsAt2PlusPercent() / 0.2, 1.0,
      0
    );
  }
  else if (discreteRating === 3.5) {
    // 3.5 → 4: 200 pts + 50% skills at 2+
    // Points: 200 → 250 (50 more points)
    // Skills: 20% → 50% (30% more needed)
    progressToNext = calculateProgressBetweenTiers(
      (totalPoints - 200) / 50, 1.0,
      (getSkillsAt2PlusPercent() - 0.2) / 0.3, 1.0,
      0
    );
  }
  else if (discreteRating === 4.0) {
    // 4 → 4.5: 250 pts + 75% skills at 2+
    // Points: 250 → 300 (50 more points)
    // Skills: 50% → 75% (25% more needed)
    progressToNext = calculateProgressBetweenTiers(
      (totalPoints - 250) / 50, 1.0,
      (getSkillsAt2PlusPercent() - 0.5) / 0.25, 1.0,
      0
    );
  }
  else if (discreteRating === 4.5) {
    // 4.5 → 5: 300 pts + 25% skills at 3
    // Points: 300 → 350 (50 more points)
    // Skills: 0% → 25% (25% more needed)
    progressToNext = calculateProgressBetweenTiers(
      (totalPoints - 300) / 50, 1.0,
      getSkillsAt3Percent() / 0.25, 1.0,
      0
    );
  }
  else if (discreteRating === 5.0) {
    // 5 → 5.5: 350 pts + 50% skills at 3
    // Points: 350 → 400 (50 more points)
    // Skills: 25% → 50% (25% more needed)
    progressToNext = calculateProgressBetweenTiers(
      (totalPoints - 350) / 50, 1.0,
      (getSkillsAt3Percent() - 0.25) / 0.25, 1.0,
      0
    );
  }
  else if (discreteRating === 5.5) {
    // 5.5 → 6: All skills maxed (100% at 3 points)
    // Points: 400 → maxPossible
    const pointsNeeded = maxPossible - 400;
    progressToNext = (totalPoints - 400) / pointsNeeded;
  }

  // Clamp progress to 0-1 range
  progressToNext = Math.max(0, Math.min(1, progressToNext));

  // Return smooth rating: discrete rating + (progress * 0.5)
  return Math.round((discreteRating + progressToNext * 0.5) * 100) / 100;
}

/**
 * Get detailed breakdown of current Playtomic rating progress
 * Shows: current rating, next milestone, points needed, percentage mastery
 */
export function getPlaytomicRatingBreakdown(ratings) {
  const { defense, attack, tactics } = getCoreBranches();
  const defenseLeaves = getLeafIds(defense);
  const attackLeaves = getLeafIds(attack);
  const tacticsLeaves = getLeafIds(tactics);
  const allLeaves = [...defenseLeaves, ...attackLeaves, ...tacticsLeaves];

  const totalPoints = Object.values(ratings).reduce((sum, v) => sum + (v || 0), 0);
  const maxPossible = allLeaves.length * 3;
  const currentRating = calculatePlaytomicRating(ratings);
  const smoothRating = calculatePlaytomicRatingSmooth(ratings);

  // Count skills at various mastery levels
  const skillsWithPoints = allLeaves.filter(id => (ratings[id] || 0) > 0).length;
  const skillsAt2Plus = allLeaves.filter(id => (ratings[id] || 0) >= 2).length;
  const skillsAt3 = allLeaves.filter(id => (ratings[id] || 0) === 3).length;

  const percentMastered = (skillsAt3 / allLeaves.length) * 100;
  const percentAt2Plus = (skillsAt2Plus / allLeaves.length) * 100;
  const percentUnlocked = (skillsWithPoints / allLeaves.length) * 100;
  const overallProgress = (totalPoints / maxPossible) * 100;

  return {
    currentRating,
    smoothRating,
    totalPoints,
    maxPossible,
    percentProgress: overallProgress,
    skillBreakdown: {
      total: allLeaves.length,
      unlocked: skillsWithPoints,
      at2Plus: skillsAt2Plus,
      at3: skillsAt3,
      percentUnlocked,
      percentAt2Plus,
      percentMastered,
    },
    defenseStats: {
      total: defenseLeaves.length,
      touched: defenseLeaves.filter(id => (ratings[id] || 0) > 0).length,
    },
    attackStats: {
      total: attackLeaves.length,
      touched: attackLeaves.filter(id => (ratings[id] || 0) > 0).length,
    },
    tacticsStats: {
      total: tacticsLeaves.length,
      touched: tacticsLeaves.filter(id => (ratings[id] || 0) > 0).length,
    },
  };
}
